import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RestaurantPreparationStationService from '../../services/restaurant/RestaurantPreparationStationService';
import RestaurantPrinterService from '../../services/restaurant/RestaurantPrinterService';
import { AppDispatch, RootState } from '../../store/store';
import {
  closeAssignments, closeStationModal, openAssignments, openStationCreate,
  openStationEdit, setStationAssignments, setStationLoading, setStationPrinters,
  setStationSaving, setStationUsers, setStations, updateAssignmentField,
  updateStationField,
} from '../../store/slices/restaurant/restaurantPreparationStationSlice';
import {
  RestaurantPreparationStationDTO, RestaurantPreparationStationForm,
  RestaurantPreparationStationFilters,
  RestaurantStationAssignmentForm,
} from '../../types/restaurant/RestaurantPreparationStationTypes';
import {
  RestaurantPrinterDTO,
  RestaurantPrinterForm,
} from '../../types/restaurant/RestaurantPrinterTypes';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useConfirm from '../useConfirmModal';

const useRestaurantPreparationStations = (branchId: number | '') => {
  const dispatch = useDispatch<AppDispatch>();
  const state = useSelector((root: RootState) => root.restaurantPreparationStation);
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const confirm = useConfirm();
  const lastFilters = useRef<RestaurantPreparationStationFilters>({ page: 0, size: 20 });

  const load = async (filters: RestaurantPreparationStationFilters = lastFilters.current) => {
    if (!branchId) return;
    lastFilters.current = filters;
    dispatch(setStationLoading(true));
    try {
      const [stations, printers] = await Promise.all([
        RestaurantPreparationStationService.list(branchId, filters),
        RestaurantPrinterService.list(branchId, { page: 0, size: 200, active: true, enabled: true }),
      ]);
      dispatch(setStations(stations));
      dispatch(setStationPrinters(printers.content));
    } catch (error) { handle(error); }
    finally { dispatch(setStationLoading(false)); }
  };

  const edit = async (station: RestaurantPreparationStationDTO) => {
    try {
      const response = await RestaurantPreparationStationService.get(station.id);
      dispatch(openStationEdit(response.data));
    } catch (error) { handle(error); }
  };

  const save = async () => {
    if (!branchId || state.saving) return;
    dispatch(setStationSaving(true));
    try {
      const response = state.form.id
        ? await RestaurantPreparationStationService.update(state.form.id, state.form)
        : await RestaurantPreparationStationService.create(branchId, state.form);
      show(response.type || 'success', response.title || 'Éxito', response.message || 'Estación guardada correctamente.');
      dispatch(closeStationModal());
      await load(lastFilters.current);
    } catch (error) { handle(error); }
    finally { dispatch(setStationSaving(false)); }
  };

  const createPrinterInline = async (
    input: Partial<RestaurantPrinterForm> & { name: string }
  ): Promise<RestaurantPrinterDTO | null> => {
    if (!branchId) return null;
    try {
      const form: RestaurantPrinterForm = {
        name: input.name,
        description: input.description ?? '',
        printerName: (input.printerName ?? input.name).trim(),
        printerType: input.printerType ?? 'KITCHEN',
        connectionType: input.connectionType ?? 'NETWORK',
        ipAddress: input.ipAddress ?? '',
        port: input.port ?? '',
        displayOrder: input.displayOrder ?? 1,
        enabled: true,
      };
      const response = await RestaurantPrinterService.create(branchId, form);
      dispatch(setStationPrinters([...state.printers, response.data]));
      show(
        response.type || 'success',
        response.title || 'Éxito',
        response.message || 'Impresora creada correctamente.'
      );
      return response.data;
    } catch (error) {
      handle(error);
      return null;
    }
  };

  const deactivate = (station: RestaurantPreparationStationDTO) => confirm({
    title: 'Desactivar estación',
    message: `¿Desea desactivar “${station.name}”? También se desactivarán sus asignaciones.`,
    icon: 'warning', confirmText: 'Sí, desactivar', cancelText: 'Cancelar',
    onConfirm: async () => {
      try {
        const response = await RestaurantPreparationStationService.deactivate(station.id);
        show(response.type || 'success', response.title || 'Éxito', response.message || 'Estación desactivada.');
        await load(lastFilters.current);
      } catch (error) { handle(error); }
    },
  });

  const manageAssignments = async (station: RestaurantPreparationStationDTO) => {
    if (!branchId) return;
    dispatch(openAssignments(station));
    dispatch(setStationLoading(true));
    try {
      const [assignments, users] = await Promise.all([
        RestaurantPreparationStationService.listAssignments(station.id, false),
        RestaurantPreparationStationService.listUsers(branchId),
      ]);
      dispatch(setStationAssignments(assignments.data ?? []));
      dispatch(setStationUsers(users.filter((user) => user.status !== false)));
    } catch (error) { handle(error); }
    finally { dispatch(setStationLoading(false)); }
  };

  const reloadAssignments = async () => {
    if (!state.selectedStation) return;
    const response = await RestaurantPreparationStationService.listAssignments(state.selectedStation.id, false);
    dispatch(setStationAssignments(response.data ?? []));
  };

  const assignUser = async () => {
    if (!state.selectedStation || !state.assignmentForm.userId || state.saving) return;
    dispatch(setStationSaving(true));
    try {
      const response = await RestaurantPreparationStationService.assign(state.selectedStation.id, state.assignmentForm);
      show(response.type || 'success', response.title || 'Éxito', response.message || 'Usuario asignado.');
      dispatch(updateAssignmentField({ field: 'userId', value: '' }));
      dispatch(updateAssignmentField({ field: 'primaryAssignment', value: false }));
      await reloadAssignments();
    } catch (error) { handle(error); }
    finally { dispatch(setStationSaving(false)); }
  };

  const updateAssignment = async (id: number, data: { primaryAssignment?: boolean; active?: boolean }) => {
    try {
      const response = await RestaurantPreparationStationService.updateAssignment(id, data);
      show(response.type || 'success', response.title || 'Éxito', response.message || 'Asignación actualizada.');
      await reloadAssignments();
    } catch (error) { handle(error); }
  };

  const deactivateAssignment = async (id: number) => {
    try {
      const response = await RestaurantPreparationStationService.deactivateAssignment(id);
      show(response.type || 'success', response.title || 'Éxito', response.message || 'Asignación desactivada.');
      await reloadAssignments();
    } catch (error) { handle(error); }
  };

  const getUserStations = async (userId: number) => {
    try {
      const response = await RestaurantPreparationStationService.listUserStations(userId);
      return response.data ?? [];
    } catch (error) {
      handle(error);
      return [];
    }
  };

  const updateField = <K extends keyof RestaurantPreparationStationForm>(field: K, value: RestaurantPreparationStationForm[K]) =>
    dispatch(updateStationField({ field, value }));
  const updateAssignmentFieldValue = <K extends keyof RestaurantStationAssignmentForm>(field: K, value: RestaurantStationAssignmentForm[K]) =>
    dispatch(updateAssignmentField({ field, value }));

  return {
    ...state, load, edit, save, deactivate, manageAssignments, assignUser,
    updateAssignment, deactivateAssignment, getUserStations, updateField,
    createPrinterInline,
    updateAssignmentField: updateAssignmentFieldValue,
    openCreate: () => dispatch(openStationCreate()),
    closeStationModal: () => dispatch(closeStationModal()),
    closeAssignments: () => dispatch(closeAssignments()),
  };
};

export default useRestaurantPreparationStations;
